import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import useMediaQuery from "@mui/material/useMediaQuery";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import Typography from "@mui/material/Typography";

// project import
import NavItem from "./NavItem";
import NavGroup from "./NavGroup";
import menuItem from "menu-items";

import useConfig from "hooks/useConfig";
import { HORIZONTAL_MAX_ITEM, MenuOrientation } from "config";
import { useGetMenuMaster } from "api/menu";
import useZustandStore from "zustandStore";

export default function Navigation() {
  const { menuOrientation } = useConfig();
  const { menuMaster } = useGetMenuMaster();
  const downLG = useMediaQuery((theme) => theme.breakpoints.down("lg"));
  const location = useLocation();

  const { serviceStatus, hideLicense } = useZustandStore();

  const [selectedID, setSelectedID] = useState("");
  const [selectedItems, setSelectedItems] = useState("");
  const [selectedLevel, setSelectedLevel] = useState(0);
  const [menuItems, setMenuItems] = useState({ items: [] });

  const [lastBreadcrumbsStr, setLastBreadcrumbsStr] = useState(
    localStorage.getItem("selectBreadcrumbs") || ""
  );

  const checkMockup = process.env.REACT_APP_IS_MOCKUP;
  const checkEnvironment = process.env.REACT_APP_RUNNING_CONTEXT;

  const parseSelectBreadcrumbs = () => {
    try {
      const raw = localStorage.getItem("selectBreadcrumbs");
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  };

  const getScope = (selectBreadcrumbs) => {
    const hasManageOrg = selectBreadcrumbs?.realm?.hasManageOrg;
    const hasOrg = !!selectBreadcrumbs?.orgs?.id;
    const hasSite = !!selectBreadcrumbs?.site?.id;

    if (hasSite) return "site";
    if (!hasSite && hasManageOrg === false) return "orgs";
    if (!hasSite && hasManageOrg === true && hasOrg) return "orgs";
    if (!hasSite && hasManageOrg === true && !hasOrg) return "realm";

    return "group";
  };

  // 根據 scope 與 roleName 決定該顯示哪些 menuItem
  // role = None，要過濾掉 users 和 settings
  const getMenuItemsByScope = (scope, selectBreadcrumbs, menuItem, serviceStatus) => {
    const selectedItems = [];

    let roleName = "";
    if (scope === "realm") roleName = selectBreadcrumbs?.realm?.roleName || "";
    else if (scope === "orgs") roleName = selectBreadcrumbs?.orgs?.roleName || "";
    else roleName = "";

    const shouldFilter = roleName === "None";

    const filterChildren = (children) =>
      children.filter((child) => {
        const isUnityRd3 = window.location.hostname.includes('unity-rd3');
        const finalHideLicense = isUnityRd3 ? false : hideLicense;

        // Feature healthAI filter
        if (isUnityRd3 && child.id === "aInsight" && !serviceStatus?.services?.healthAI) {
          return false;
        }

        // Feature license-management filter
        if (child.id === "license-management" && finalHideLicense) {
          return false;
        }

        // Role None filter
        if (shouldFilter) {
          return child.id !== "users" && child.id !== "settings";
        }

        // Permissions filter
        if (child.permissions && scope === "site") {
          return child.permissions.includes(selectBreadcrumbs?.site?.roleName);
        }

        return true;
      });

    const findAndPushItem = (id) => {
      const item = menuItem.items.find((i) => i.id === id);
      if (item) {
        const clonedItem = { ...item };
        if (Array.isArray(clonedItem.children)) {
          clonedItem.children = filterChildren(clonedItem.children);
        }
        selectedItems.push(clonedItem);
      }
    };

    if (scope === "realm") findAndPushItem("orgs-scope-pages");
    else if (scope === "orgs") findAndPushItem("site-scope-pages");
    else findAndPushItem("group-pages");

    findAndPushItem("other");

    return { items: selectedItems };
  };

  const updateMenuFromLocalStorage = () => {
    const selectBreadcrumbs = parseSelectBreadcrumbs();
    const scope = getScope(selectBreadcrumbs);
    const currentMenuItems = getMenuItemsByScope(scope, selectBreadcrumbs, menuItem, serviceStatus);
    setMenuItems(currentMenuItems);
  };

  useEffect(() => {
    updateMenuFromLocalStorage();

    const interval = setInterval(() => {
      const currentStr = localStorage.getItem("selectBreadcrumbs") || "";
      if (currentStr !== lastBreadcrumbsStr) {
        setLastBreadcrumbsStr(currentStr);
        updateMenuFromLocalStorage();
      }
    }, 100);

    return () => clearInterval(interval);
  }, [location.pathname, lastBreadcrumbsStr, serviceStatus]);

  const isHorizontal = menuOrientation === MenuOrientation.HORIZONTAL && !downLG;

  const lastItem = isHorizontal ? HORIZONTAL_MAX_ITEM : null;
  let lastItemIndex = menuItems.items.length - 1;
  let remItems = [];
  let lastItemId;

  if (lastItem && lastItem < menuItems.items.length) {
    lastItemId = menuItems.items[lastItem - 1]?.id;
    lastItemIndex = lastItem - 1;
    remItems = menuItems.items
      .slice(lastItem - 1)
      .map((item) => ({
        title: item.title,
        elements: item.children,
        icon: item.icon,
        ...(item.url && { url: item.url }),
      }));
  }

  const navGroups = menuItems.items
    .slice(0, lastItemIndex + 1)
    .map((item, index) => {
      switch (item.type) {
        case "group":
          if (item.url && item.id !== lastItemId) {
            return (
              <List key={item.id} {...(isHorizontal && { sx: { mt: 0.5 } })}>
                {!isHorizontal && index !== 0 && <Divider sx={{ my: 0.5 }} />}
                <NavItem
                  item={item}
                  level={1}
                  isParents
                  setSelectedID={setSelectedID}
                />
              </List>
            );
          }

          return (
            <NavGroup
              key={item.id}
              setSelectedID={setSelectedID}
              setSelectedItems={setSelectedItems}
              setSelectedLevel={setSelectedLevel}
              selectedLevel={selectedLevel}
              selectedID={selectedID}
              selectedItems={selectedItems}
              lastItem={lastItem}
              remItems={remItems}
              lastItemId={lastItemId}
              item={item}
            />
          );

        default:
          return (
            <Typography key={item.id} variant="h6" color="error" align="center">
              Fix - Navigation Group
            </Typography>
          );
      }
    });

  return <Box>{navGroups}</Box>;
}
